import file_upload from './file-upload';
import backgroundOperations from './background-operations';
import { features } from './features';
import { strings } from './shared-strings';

export default {
  features,
  backgroundOperations,
  iocTypes: {
    url: 'URL',
    md5: 'MD5',
    sha1: 'SHA1',
    sha256: 'SHA256',
    hash: 'Hash',
    domain: 'Domain',
    'domain-name': 'Domain',
    subdomain: 'Subdomain',
    'ipv4-addr': 'IP',
    ipv4: 'IP',
    ip: 'IP'
  },
  severity: {
    info: 'Info',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    very_high: 'Very High'
  },
  score: {
    A: 'Very Good',
    B: 'Good',
    C: 'Moderate',
    D: 'Poor',
    F: 'Very Poor',
    N_A: 'Score was not calculated yet',
    n_a_short: 'N/A'
  },
  consumption: {
    tooltip: {
      asmAssetsUtilization: 'ASM Assets utilization refers to the number of assets currently in use in the ASM module.',
      tiAssetsUtilization: 'TI Assets utilization refers to the number of assets currently in use in the TI module.',
      creditUtilization:
        'Credit utilization refers to the credits used for assets currently in use across both the ASM and TI modules.',
      coinsUtilization:
        'Coins utilization refers to the total number of coins redeemed for purchase, takedown, and investigation requests within the subscription contract period.',
      vendorsUtilization:
        'Vendors utilization refers to the number of vendors currently monitored in the Supply Chain module.',
      usersUtilization: 'Users utilization refers to the number of active users configured in Argos.',
      thUsersUtilization: 'Threat Hunters Users utilization.',
      thCyberintUsersTooltip: 'Cyberint user (ends with "@cyberint.com") does not consume Threat Hunting quota',
      riCustomAlertRules:
        'Raw Intelligence Custom Alert Rules utilization refers to the number of alert rules created for monitoring Raw Intelligence data.',
      dlCustomAlertRules:
        'Data Lake Custom Alert Rules utilization refers to the number of alert rules created for monitoring Data Lake queries.',
      warningLicenseLimit: 'You are reaching your license limit',
      reportTenantsUtilization:
        'Report Tenants utilization refers to the number of report subtenants created within the subscription contract period.',
      fallbackTooltip: 'The utilization data shown are as of May 1st, 23:55.',
      apiAndFeeds: {
        title: 'This section indicates whether access is granted to the following capabilities:',
        tier1: 'A continuously updated stream of critical IOCs enriched with context and metadata',
        tier2: 'A continuously updated stream of high severity IOCs enriched with context and metadata',
        tier3: 'A continuously updated stream of all Check Point observed IOCs enriched with context and metadata',
        enrichmentApi:
          'A real-time API that enriches IOCs with deep threat context, relationships, and risk insights to accelerate investigation, triage, and response workflows'
      },
      whiteLabel: 'This section indicates whether access to White Label capabilities are granted for this tenant.'
    },
    title: {
      users: 'Users capacity',
      thUsers: 'Threat Hunters',
      credits: 'Credit capacity',
      asmAssets: 'ASM Assets',
      tiAssets: 'TI Assets',
      coins: 'Coins usage',
      vendors: 'Vendors usage',
      alertRules: 'Alert Rules capacity'
    }
  },
  common: {
    yes: 'Yes',
    no: 'No',
    selected: 'Selected %{count}',
    select: 'Select value',
    selectOrAdd: 'Select value or add new',
    postfix_selected: 'selected',
    genericThreatMsg:
      'This Threat Actor was automatically linked to the intel item based on previous indications of threat activity.',
    insufficientCoins:
      'You do not have enough coins(%{cost}) to perform this action. Please contact your administrator to allocate more coins.',
    insufficientCoinsTitle: 'Insufficient Coins',
    copy: 'Copy',
    copyAll: 'Copy All',
    copied: 'Copied!',
    selectFile: 'Select File',
    na: 'N/A',
    none: 'None',
    irrelevant: 'Irrelevant',
    noPreview: 'No preview',
    loading: 'Loading ...',
    search: 'Search',
    refresh: 'Refresh',
    sources: 'Sources',
    refreshMsg: 'Refresh the browser to see the updated data',
    filters: 'Filters',
    clearAll: 'Clear All',
    showCountMore: 'Show %{count} More',
    abort: 'Abort',
    grandTotal: 'Grand total',
    including: 'Including',
    noData: 'No Data',
    start_date: 'Start Date',
    end_date: 'End Date',
    created_date: 'Created Date',
    updated_date: 'Updated Date',
    expired: 'Expired',
    noExpiration: 'No Expiration',
    willExpireDays: 'Will expire in %{days} days',
    unAuthPage:
      "Your current role doesn't include permission for this resource. Return home or ask your administrator for access.",
    unAuth: "You don't have access to this page",
    homePage: 'Home Page',
    failedToDownloadReport:
      'There was an issue downloading report, and your request could not be processed at this time.',
    failedToFetchLicense:
      'There was an issue fetching licensing data, and your request could not be processed at this time. Please ensure that your internet connection is stable and try again.',
    download_dangerous_file_title: 'Download Un-Safe File',
    download_dangerous_file_msg:
      'You are about to download a file that might be dangerous. Are you sure you wish to continue?',
    download_dangerous_file: 'Preview not available for this file. It might be dangerous',
    no_preview_dangerous_file: 'Preview not available for this file. It might be dangerous',
    dangerousUrlTitle: 'Go To URL',
    dangerousUrlMsg: 'You are going to another website, which can be dangerous!',
    open_in_new_tab: 'Open in new tab',
    preview_not_available_for_file_type: 'Preview not available for this file type',
    typedFile: '%{type} file',
    failed_to_load_file: 'Failed to load file',
    loading_file: 'Loading file...',
    contactSupport: 'Contact Support',
    notFound: 'Not Found',
    featureNotAvailableInRegion: 'This feature is not supported in your region.'
  },
  error: {
    generic: 'Something went wrong.',
    fileSelect: 'Failed to select or load file',
    failedFetch: 'Failed to fetch data',
    genericLater: 'Something went wrong, please try again later.',
    failedDownload: 'Failed to download file',
    failed_export_csv: 'Failed to export CSV file'
  },
  validation: {
    exceeded: '%{count} Exceeded',
    numeric: 'Must be a number',
    email: 'Must be a valid email address',
    numericPositive: 'Must be a positive number',
    numericRange: 'Must be a number between %{min} to %{max}',
    maxLimit: 'Must be less than %{limit} characters',
    invalidDateRange: 'Start Date must precede End Date',
    value_length: 'Value cannot exceed %{maxLength} characters',
    required: 'Required value',
    cannot_be_empty: "%{type} value can't be empty",
    input_not_valid: '%{type} is not valid',
    sha256: 'Must be valid sha256'
  },
  buttons: {
    add: 'Add',
    cancel: 'Cancel',
    submit: 'Submit',
    download: 'Download',
    close: 'Close',
    clear: 'Clear',
    clearFilters: 'Clear Filters',
    maximize: 'Maximize',
    restore: 'Restore',
    create: 'Create',
    ok: 'OK',
    apply: 'Apply',
    revert: 'Revert',
    minimize: 'Minimize',
    save: 'Save',
    send: 'Send',
    delete: 'Delete',
    edit: 'Edit',
    share: 'Share',
    exportPdf: 'Export to PDF',
    exportXlsx: 'Export to XLSX',
    remove: 'Remove',
    confirm: 'Confirm'
  },
  dateRange: {
    lastD: 'Last Day',
    last2D: 'Last 2 Days',
    lastW: 'Last Week',
    last2W: 'Last 2 Weeks',
    lastM: 'Last Month',
    last2M: 'Last 2 Months',
    last3M: 'Last 3 Months',
    last6M: 'Last 6 Months',
    lastY: 'Last Year',
    today: 'Today',
    tomorrow: 'Tomorrow',
    in1W: 'Expires within 1 Week',
    in2W: 'Expires within 2 Weeks',
    in1M: 'Expires within 1 Month',
    in3M: 'Expires within 3 Months',
    expired: 'Expired',
    daysAmount: '%{smart_count} Day |||| %{smart_count} Days',
    weeksAmount: '%{smart_count} Week |||| %{smart_count} Weeks',
    monthsAmount: '%{smart_count} Month |||| %{smart_count} Months',
    yearsAmount: '%{smart_count} Year |||| %{smart_count} Years',
    all: 'All time'
  },
  parkReason: {
    ns_record_in_list: 'NS record matches a known parked domain provider',
    get_title: 'Website title matches a known parked domain provider',
    get_content: 'Website content matches a known parked domain provider',
    headers_lander_type_parkweb: 'Headers indicate the website is a parked domain (contains "parkweb")',
    headers_lander_type_parking: 'Headers indicate the website is a parked domain (contains "parking")',
    headers_lander_type_forwarder: 'Headers indicate the website is a parked domain (contains "forwarder")'
  },
  vendor_categories: {
    agriculture: 'Agriculture',
    business_services: 'Business Services',
    holding_companies_and_conglomerates: 'Holding Companies & Conglomerates',
    construction: 'Construction',
    consumer_services: 'Consumer Services',
    education: 'Education',
    energy_utilities_and_waste: 'Energy, Utilities & Waste',
    finance: 'Finance',
    government: 'Government',
    healthcare_services: 'Healthcare Services',
    hospitality: 'Hospitality',
    hospitals_and_physicians_clinics: 'Hospitals & Physicians Clinics',
    insurance: 'Insurance',
    law_firms_and_legal_services: 'Law Firms & Legal Services',
    media_and_internet: 'Media & Internet',
    manufacturing: 'Manufacturing',
    minerals_and_mining: 'Minerals & Mining',
    organizations: 'Organizations',
    real_estate: 'Real Estate',
    retail: 'Retail',
    software: 'Software',
    telecommunications: 'Telecommunications',
    transportation: 'Transportation'
  },
  risk_factors: {
    recently_attacked: 'Recently Attacked',
    trending_news: 'Trending News',
    exposed_credentials: 'Exposed Credentials',
    employee_creds_offered_for_sale: 'Credentials for Sale',
    crime_chatter: 'Crime Chatter',
    phishing_campaign: 'Phishing Campaign',
    recently_breached: 'Recently Breached',
    exploitable_ports: 'Exposed Ports',
    hijackable_subdomains: 'Hijackable Subdomains',
    open_web_interface: 'Open Web Interface',
    mail_servers_in_blocklist: 'Mail Server in Blocklist',
    ssl_tls_issues: 'TLS/SSL',
    email_security_issues: 'Email Security',
    certificate_authority_issues: 'Certificate'
  },
  investigation_status: {
    initiated: 'Initiated',
    received: 'Received',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled'
  },
  investigation_channel: {
    alert_discussion_board: 'Alert Discussion Board',
    alert: 'Alert',
    email: 'Email',
    meetings: 'Meeting',
    support_ticket: 'Support Ticket'
  },
  investigation_type: {
    vip_investigation: 'VIP Investigation',
    threat_landscape_report: 'Threat Landscape Report',
    industry_expertise: 'Industry Expertise',
    regional_expertise: 'Regional Expertise',
    incident_report: 'Incident Report',
    alert_report: 'Alert Report',
    threat_actor_investigation: 'Threat Actor Investigation',
    purchase: 'Purchase',
    threat_investigation: 'Threat Investigation',
    phishing_campaign: 'Phishing Campaign Investigation',
    humint_investigation: 'HUMINT Investigation',
    social_media_mapping: 'Social Media Mapping',
    other: 'Other',
    vip_investigation_description:
      'Focused examination and analysis of potential threats or risks specifically targeting high-profile individuals or entities.',
    threat_landscape_report_description:
      'Comprehensive overview of the current cybersecurity threat environment, highlighting emerging risks and trends.',
    industry_expertise_description:
      'Report covering trends/phenomena about the particular business sector, enabling tailored threat assessments and security measures relevant to that industry.',
    regional_expertise_description:
      'Report covering trends/phenomena about the particular geographic region, enabling tailored threat assessments and security measures relevant to that region.',
    incident_report_description:
      'Detailed analysis of a cybersecurity incident, outlining the events, impact, and suggested mitigation strategies.',
    alert_report_description: 'A comprehensive examination of an incident or use-case outlined in an alert.',
    threat_actor_investigation_description:
      'In-depth examination of the entities or individuals responsible for cybersecurity threats, aiming to identify motives and tactics.',
    purchase_description:
      'Acquisition of tools, databases, or any other items, with the possibility of including analysis of the acquired tools.',
    threat_investigation_description:
      'Systematic inquiry into potential cybersecurity threats, involving the identification, analysis, and mitigation of risks.',
    phishing_campaign_description:
      'Examination and analysis of phishing attacks, including the identification of tactics, techniques, and potential perpetrators.',
    humint_investigation_description:
      'Human Intelligence investigation, involving the collection and analysis of information obtained through human sources.',
    social_media_mapping_description:
      'Exploration and analysis of social media platforms to identify potential security risks and gather intelligence.',
    other_description: 'Additional types of investigations or reports not covered by the specified terms.'
  },
  release_notes: {
    go_to_release_notes: 'Read more',
    new_release_notes: 'New updates!',
    failed_to_fetch_user_config: 'Failed to fetch or update user configuration'
  },
  online_help: {
    tooltip: 'API & System Admin Documentation'
  },
  tenant_types: {
    mssp_management: 'MSSP Management',
    mssp_standalone_allocated: 'MSSP Production Tenant (Standalone Credits)',
    mssp_parent_allocated: 'MSSP Production Tenant (Inherited Credits)',
    mssp_provisioning: 'MSSP Provisioning',
    mssp_report: 'MSSP Report',
    cyberint_customer: 'Cyberint Customer',
    cyberint_management: 'Cyberint Management',
    cyberint_provisioning: 'Cyberint Provisioning',
    partners_var_management: 'Partner Management',
    partners_var_provisioning: 'Partner Provisioning',
    multi_tenant_management: 'Multi Tenant Management'
  },
  engagement_types: {
    asm: 'ASM',
    brand_abuse: 'Brand Protection (Brand Abuse)',
    darkweb: 'DarkWeb',
    bronze: 'Essential (Bronze)',
    silver: 'Advanced (Silver)',
    gold: 'Complete (Gold)',
    platinum: 'Elite (Platinum)',
    demo: 'Demo',
    poc: 'POC Managed',
    unmanaged_poc: 'POC Self-Service',
    sizing: 'Sizing',
    report: 'Report',
    internal_use: 'Internal Use',
    production_customer: 'Production Customer',
    tem_essentials: 'TEM Essentials',
    mssp_management: 'MSSP Management',
    mssp_management_poc: 'MSSP Management POC',
    mssp_management_demo: 'MSSP Management Demo',
    mssp_management_unmanaged_poc: 'MSSP Management POC Self-Service'
  },
  empty_state: {
    no_matches: 'No matches were found.'
  },
  confidence_levels: {
    25: 'Attack Denied by the Company',
    50: 'Threat Actor Publicly Claimed Responsibility (No Evidence)',
    75: 'Threat Actor Provided Evidence of the Attack',
    100: 'Public Confirmation by the Victim'
  },
  non_targeted_alerts: {
    category: {
      incident: 'Cyber Attacks',
      news: 'Cyber News',
      reports: 'Reports',
      threat_landscape_ai_report: 'AI Threat Landscape Reports'
    }
  },
  source_category_info: strings.source_category_info,
  brand_logo_config_disclaimer: strings.brand_logo_config_disclaimer,
  file_upload
};
